
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logSecurityEvent } from '@/utils/securityAudit';

interface RateLimitOptions {
  identifier: string;
  action: string;
  maxAttempts: number;
  windowMinutes: number;
}

export const useRateLimit = () => {
  const [isBlocked, setIsBlocked] = useState(false);

  const checkRateLimit = useCallback(async (options: RateLimitOptions): Promise<boolean> => {
    try {
      const { identifier, action, maxAttempts, windowMinutes } = options;
      const windowStart = new Date(Date.now() - windowMinutes * 60 * 1000);

      // Check existing attempts
      const { data: existing, error: fetchError } = await supabase
        .from('rate_limits')
        .select('attempts, window_start')
        .eq('identifier', identifier)
        .eq('action', action)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      let currentAttempts = 1;
      let shouldBlock = false;

      if (existing) {
        const existingWindowStart = new Date(existing.window_start);
        
        if (existingWindowStart > windowStart) {
          // Within rate limit window
          currentAttempts = existing.attempts + 1;
          shouldBlock = currentAttempts > maxAttempts;

          await supabase
            .from('rate_limits')
            .update({ attempts: currentAttempts })
            .eq('identifier', identifier)
            .eq('action', action);
        } else {
          // Reset window
          await supabase
            .from('rate_limits')
            .update({ 
              attempts: 1, 
              window_start: new Date().toISOString() 
            })
            .eq('identifier', identifier)
            .eq('action', action);
        }
      } else {
        // Create new rate limit entry
        await supabase
          .from('rate_limits')
          .insert([{
            identifier,
            action,
            attempts: 1,
            window_start: new Date().toISOString()
          }]);
      }

      if (shouldBlock) {
        await logSecurityEvent({
          event_type: 'RATE_LIMIT_EXCEEDED',
          details: { identifier, action, attempts: currentAttempts }
        });
        setIsBlocked(true);
      }

      return shouldBlock;
    } catch (error) {
      console.error('Rate limit check failed:', error);
      return false; // Fail open for better UX
    }
  }, []);

  return { checkRateLimit, isBlocked };
};
